import { BaseComponent, BuildingModel, BuildingStructure, LevelModel, PhotoModel } from '../types/protocols';
import { BaseType } from '../types/Enums';
import { resolveComponentLocation } from './ComponentRoomUtils';
import { levelDisplayName } from './levelDisplayName';
import { MOUNTING_SURFACE_LABELS, ROOF_PLAN_LABEL, currentRoofPlanBlueprint, hasRoofPlanView, isRoofPlanComponent, liveRoofKeys } from './roofPlan';
import { SATELLITE_LABEL } from './mapLocations';

/**
 * Which picture a component belongs on, and whether it is placed there.
 *
 * A component's *homes* are the pictures it is expected to carry a pin on:
 * the drawing of its room's primary level, the roof drawing (takplan) when
 * it is a roof or sits on one, and the satellite image for things outside
 * the building. A pin elsewhere still renders wherever it exists; the homes
 * only decide what counts as "not placed yet". Pure helpers: no Firebase.
 */

export type DrawingHome =
    | { kind: 'level'; levelKey: string }
    | { kind: 'roofPlan' }
    | { kind: 'satellite' };

export type DrawingViewKind = DrawingHome['kind'];

/**
 * One picture to look at: the satellite image, the roof drawing or a level's
 * drawing. `imageKey`/`image` are null when the owner has no current image
 * (a level without a drawing is still a view, its components all unplaced).
 */
export interface DrawingView {
    kind: DrawingViewKind;
    /** Set for `kind: 'level'`. */
    levelKey?: string;
    imageKey: string | null;
    image: PhotoModel | null;
    label: string;
}

/** Base types whose natural picture is the satellite image whatever they are attached to. */
const SATELLITE_HOME_TYPES = new Set<BaseType>([
    BaseType.Roof,
    BaseType.Facade,
    BaseType.Outdoor,
    BaseType.GroundDrainage,
]);

/** A view's identity for comparisons: level views differ by level, the other kinds are singletons. */
const sameHome = (home: DrawingHome, view: Pick<DrawingView, 'kind' | 'levelKey'>): boolean =>
    home.kind === view.kind && (home.kind !== 'level' || home.levelKey === view.levelKey);

/**
 * Utilities and Övrigt that are neither in a room nor on a roof: outdoor
 * (no parent) or facade-mounted. Roof-mounted ones belong to the takplan,
 * and anything with a room (even one that no longer exists) is indoors.
 */
const isOutsideMounted = (component: BaseComponent, hasRoom: boolean, structure: BuildingStructure | undefined): boolean => {
    if (component.type !== BaseType.Utility && component.type !== BaseType.Miscellaneous) return false;
    if (hasRoom || (component.parentRoomKeys?.value?.length ?? 0) > 0) return false;
    const parentKey = component.parentSurface?.value;
    const parent = parentKey ? structure?.components?.[parentKey] : undefined;
    return !parent || parent.type === BaseType.Facade;
};

/**
 * The pictures `component` belongs on, in display order (level, takplan,
 * satellite). A roof is home on both the roof drawing and the satellite
 * image. Empty for components placed only in photos (a facade window) or
 * in a room whose level is gone. `roofKeys` can be precomputed with
 * `liveRoofKeys` when resolving many components.
 */
export const componentHomes = (
    component: BaseComponent,
    structure: BuildingStructure | undefined,
    roofKeys: Set<string> = liveRoofKeys(structure)
): DrawingHome[] => {
    const homes: DrawingHome[] = [];
    const location = resolveComponentLocation(component, structure);
    if (location.levelKey) homes.push({ kind: 'level', levelKey: location.levelKey });
    if (isRoofPlanComponent(component, roofKeys)) homes.push({ kind: 'roofPlan' });
    if (SATELLITE_HOME_TYPES.has(component.type) || isOutsideMounted(component, location.roomKey !== null, structure)) {
        homes.push({ kind: 'satellite' });
    }
    return homes;
};

/** How a list groups a component below its base type: by level, takplan, facade or outdoors. */
export interface ListGroup {
    /** The level key, or one of the reserved `__…__` keys below (never a push key). */
    key: string;
    label: string;
}

export const ROOF_PLAN_GROUP_KEY = '__roof_plan__';
export const FACADE_GROUP_KEY = '__mounted_facade__';
export const OUTDOOR_GROUP_KEY = '__outdoor__';
export const OUTDOOR_LABEL = 'Utomhus';

/**
 * The group a list puts `component` in under its base type, from its first
 * home: its level (a takfönster, home on both, is listed by its level and
 * only drawn on the takplan), else the takplan, else outdoors. A utility or
 * Övrigt mounted on a facade keeps the "Fasad" group the other lists give
 * it. Null when the component has no home ("Utan plan").
 */
export const listGroupForComponent = (
    component: BaseComponent,
    structure: BuildingStructure | undefined,
    roofKeys?: Set<string>
): ListGroup | null => {
    const home = componentHomes(component, structure, roofKeys)[0];
    if (!home) return null;
    switch (home.kind) {
        case 'level': {
            const level = structure?.levels?.[home.levelKey];
            return { key: home.levelKey, label: level ? levelDisplayName(level) : home.levelKey };
        }
        case 'roofPlan':
            return { key: ROOF_PLAN_GROUP_KEY, label: ROOF_PLAN_LABEL };
        case 'satellite': {
            const parentKey = component.parentSurface?.value;
            const onFacade = !!parentKey && structure?.components?.[parentKey]?.type === BaseType.Facade;
            return onFacade
                ? { key: FACADE_GROUP_KEY, label: MOUNTING_SURFACE_LABELS.facade }
                : { key: OUTDOOR_GROUP_KEY, label: OUTDOOR_LABEL };
        }
    }
};

/** True when one of `homes` is the picture `view` shows. */
export const homesInclude = (homes: DrawingHome[], view: Pick<DrawingView, 'kind' | 'levelKey'>): boolean =>
    homes.some(home => sameHome(home, view));

/**
 * The component carries a pin on exactly this image: `mapLocations` for the
 * satellite image, `blueprintLocations` for a drawing. A pin on a replaced
 * image does not count, and a view without an image places nothing.
 */
export const isPlacedOn = (
    component: Pick<BaseComponent, 'blueprintLocations' | 'mapLocations'>,
    view: Pick<DrawingView, 'kind' | 'imageKey'>
): boolean => {
    if (!view.imageKey) return false;
    const pins = view.kind === 'satellite' ? component.mapLocations : component.blueprintLocations;
    return !!pins?.[view.imageKey];
};

export interface DrawingPartition<T extends BaseComponent> {
    /** Components with a pin on the view's image, whatever their homes. */
    placed: T[];
    /** Components whose homes include this view but that carry no pin on its image. */
    unplaced: T[];
}

/** Split `components` for one view: what to draw on it, and what still belongs there but is not pinned. */
export const partitionForView = <T extends BaseComponent>(
    components: T[],
    view: DrawingView,
    structure: BuildingStructure | undefined
): DrawingPartition<T> => {
    const roofKeys = liveRoofKeys(structure);
    const placed: T[] = [];
    const unplaced: T[] = [];
    components.forEach(component => {
        if (isPlacedOn(component, view)) placed.push(component);
        else if (homesInclude(componentHomes(component, structure, roofKeys), view)) unplaced.push(component);
    });
    return { placed, unplaced };
};

/** The view of a level, with its current drawing when it has one. */
export const levelDrawingView = (level: LevelModel): DrawingView => {
    const imageKey = level.currentBlueprintKey ?? null;
    const image = imageKey ? level.blueprints?.[imageKey] ?? null : null;
    return { kind: 'level', levelKey: level.key, imageKey: image ? imageKey : null, image, label: levelDisplayName(level) };
};

/** The roof drawing view; `hasRoofPlanView` says whether to offer it. */
export const roofPlanDrawingView = (structure: BuildingStructure | undefined): DrawingView => {
    const roof = currentRoofPlanBlueprint(structure);
    return { kind: 'roofPlan', imageKey: roof?.key ?? null, image: roof?.image ?? null, label: ROOF_PLAN_LABEL };
};

/** The satellite view; null when the building has no current map image. */
export const satelliteDrawingView = (model: BuildingModel | undefined): DrawingView | null => {
    const imageKey = model?.currentMapImageKey;
    const image = imageKey ? model?.mapImages?.[imageKey] : undefined;
    if (!imageKey || !image) return null;
    return { kind: 'satellite', imageKey, image, label: SATELLITE_LABEL };
};

/**
 * Every picture of a building a component can be looked for on: the
 * satellite image (when there is one), every level ascending by number
 * (with or without a drawing) and the takplan (when it has anything to show).
 */
export const drawingViews = (model: BuildingModel | undefined): DrawingView[] => {
    const structure = model?.structure;
    const views: DrawingView[] = [];
    const satellite = satelliteDrawingView(model);
    if (satellite) views.push(satellite);
    Object.entries(structure?.levels || {})
        .map(([key, level]) => (level.key ? level : { ...level, key }))
        .sort((a, b) => a.number - b.number || a.key.localeCompare(b.key))
        .forEach(level => views.push(levelDrawingView(level)));
    if (hasRoofPlanView(structure)) views.push(roofPlanDrawingView(structure));
    return views;
};

/** Where to start: the lowest level that has a drawing, else the satellite image, else the first view. */
export const defaultDrawingView = (views: DrawingView[]): DrawingView | null =>
    views.find(view => view.kind === 'level' && view.image) ??
    views.find(view => view.kind === 'satellite') ??
    views[0] ??
    null;

/** Resolve a stored home to the view that currently shows it (null when the level is gone). */
export const viewForHome = (home: DrawingHome, model: BuildingModel | undefined): DrawingView | null => {
    const structure = model?.structure;
    switch (home.kind) {
        case 'satellite':
            return satelliteDrawingView(model);
        case 'roofPlan':
            return roofPlanDrawingView(structure);
        case 'level': {
            const level = structure?.levels?.[home.levelKey];
            return level ? levelDrawingView(level.key ? level : { ...level, key: home.levelKey }) : null;
        }
    }
};

/** The homes of `component` it carries no pin on (the level/roof drawing or map image current for that home). */
export const unplacedHomes = (
    component: BaseComponent,
    model: BuildingModel | undefined,
    roofKeys?: Set<string>
): DrawingHome[] =>
    componentHomes(component, model?.structure, roofKeys).filter(home => {
        const view = viewForHome(home, model);
        return !view || !isPlacedOn(component, view);
    });

/**
 * `BlueprintWithMarkers` reads pins from `blueprintLocations[imageKey]`. To
 * draw the satellite image with it, the placed components are shadowed so
 * their `mapLocations[mapImageKey]` pin appears under `blueprintLocations`;
 * the prototype chain keeps the component's methods and other fields.
 */
export const asSatelliteMarkers = <T extends BaseComponent>(components: T[], mapImageKey: string): T[] =>
    components
        .filter(component => !!component.mapLocations?.[mapImageKey])
        .map(component => {
            const shadow = Object.create(component) as T;
            shadow.blueprintLocations = { [mapImageKey]: component.mapLocations![mapImageKey] };
            return shadow;
        });
